import type { ScenarioCard } from '../data/types';
import { useSwipe } from '../hooks/useSwipe';
import styles from './ProfileCard.module.css';

interface ProfileCardProps {
  card: ScenarioCard;
  onCommit: (direction: 'left' | 'right') => void;
}

const KIND_LABEL: Record<ScenarioCard['kind'], string> = {
  profile: 'dating profile',
  message: 'message',
  behavior: 'behavior',
  quote: 'quote',
};

export function ProfileCard({ card, onCommit }: ProfileCardProps) {
  const { state, bind } = useSwipe({ threshold: 100, onCommit });
  const { dx, dragging, thrown } = state;

  // Rotation in degrees: gentle tilt with the drag, up to ~15°
  const rotation = Math.max(-15, Math.min(15, dx / 12));
  const transform = thrown
    ? `translateX(${dx}px) rotate(${dx / 8}deg)`
    : `translateX(${dx}px) rotate(${rotation}deg)`;
  const opacity = thrown ? 0 : Math.max(0.3, 1 - Math.abs(dx) / 500);

  // Edge tints — green right, red left
  const rightTint = Math.max(0, Math.min(1, dx / 150));
  const leftTint = Math.max(0, Math.min(1, -dx / 150));

  return (
    <article
      className={`${styles.card} ${dragging ? styles.dragging : ''} ${thrown ? styles.thrown : ''}`}
      style={{ transform, opacity }}
      role="article"
      aria-label={`Scenario: ${card.kind} from ${card.name}`}
      tabIndex={0}
      {...bind}
    >
      {/* Edge tints */}
      <div
        className={styles.edgeGreen}
        aria-hidden="true"
        style={{ opacity: rightTint }}
      >
        ✓ green flag
      </div>
      <div
        className={styles.edgeRed}
        aria-hidden="true"
        style={{ opacity: leftTint }}
      >
        ✗ red flag
      </div>

      <header className={styles.header}>
        <span className={styles.kind}>{KIND_LABEL[card.kind]}</span>
        <h2 className={styles.name}>
          {card.name}
          {card.age != null ? <span className={styles.age}>, {card.age}</span> : null}
        </h2>
        {card.context && <p className={styles.context}>{card.context}</p>}
      </header>

      <p className={styles.content}>{card.content}</p>

      <footer className={styles.footer}>
        <span className={styles.swipeHintLeft}>← red</span>
        <span className={styles.swipeHintRight}>green →</span>
      </footer>
    </article>
  );
}
